// T-330 — the one demo scenario the mode menu offers, and the one place the app navigates to it.
//
// `?demoScenario=returning` (core `variants.json`, BUG-194 / REQ-023 AC1 v2.6): the demo server pins the visit
// mark to the story's Sun, Sep 20, 8:40 am, so Home opens on "Since your last visit" on every load — the panel
// D-104 (3) otherwise shows once, on the first load of a fresh profile, and then not for 12 h. REQ-023 AC1 says
// the panel "is not met until someone can see it by clicking"; this is the click.
//
// **Why a navigation and not a state change** — `dataset.ts`'s reasoning, unchanged: the app has exactly ONE
// `ApiClient`, built once from the address before React starts, and the scenario is one of its inputs. So the
// address is the truth about which scenario is on screen (`useAppConfig().qa.demoScenario`), and the way out is
// the same address without the switch.
//
// **What the address keeps.** Unlike the account switch, every other parameter stays: the gallery opens the demo
// at a pinned `?demoNow=`, and a reader who clicks this control must not fall off the story's clock. Only the
// scenario moves — in the search part AND in the hash's own query (`readQaParams` honours both), so a stale one
// in either place cannot shadow the choice.

import { QA_SWITCH_NAMES, type QaSwitchName } from './config';

/** The scenario key, as `packages/core/data/tables/variants.json` spells it. */
export const RETURNING_SCENARIO = 'returning';

const SCENARIO_PARAM: QaSwitchName = QA_SWITCH_NAMES[1];

/** A hash like `#/home?demoScenario=x&demoNow=…` → `#/home?demoNow=…` (or `#/home`); any other hash unchanged. */
function hashWithout(hash: string, name: string): string {
  const mark = hash.indexOf('?');
  if (mark < 0) return hash;
  const params = new URLSearchParams(hash.slice(mark + 1));
  params.delete(name);
  const query = params.toString();
  return `${hash.slice(0, mark)}${query ? `?${query}` : ''}`;
}

/**
 * The address the choice becomes: the current one with `demoScenario` set (or removed, for `null`), everything
 * else — the clock, the dataset, the hash route — exactly as it is.
 */
export function scenarioHref(
  location: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>,
  scenario: string | null,
): string {
  const params = new URLSearchParams(location.search);
  if (scenario === null) params.delete(SCENARIO_PARAM);
  else params.set(SCENARIO_PARAM, scenario);
  const search = params.toString();
  return `${location.origin}${location.pathname}${search ? `?${search}` : ''}${hashWithout(location.hash, SCENARIO_PARAM)}`;
}

/**
 * Go to a scenario (or back to the story with `null`). `location.assign` lives behind this seam for the reason
 * `reloadPage` and `goToDataset` do: jsdom's `Location` is not configurable, so a test watches the seam.
 */
export function goToScenario(scenario: string | null): void {
  window.location.assign(scenarioHref(window.location, scenario));
}
